@import '../../utils/control-inactive.css';
@import '../../utils/field-stack.css';
@import '../../utils/forced-colors.css';

:host {
  --ds-slider-control-size: var(--dimension-size-400);
  --ds-slider-thumb-size: var(--dimension-size-200);
  --ds-slider-track-size: var(--dimension-size-100);
  --ds-slider-track-stroke: var(--dimension-stroke-width-012);
  --ds-slider-vertical-length: calc(var(--dimension-size-800) * 3);
  box-sizing: border-box;
  width: 100%;
  color: var(--color-foreground-primary);
}

:host(.slider--sm) {
  --ds-slider-control-size: var(--dimension-size-300);
  --ds-slider-thumb-size: var(--dimension-size-150);
  --ds-slider-track-size: var(--dimension-size-075);
}

:host(.slider--xs) {
  --ds-slider-control-size: var(--dimension-size-200);
  --ds-slider-thumb-size: var(--dimension-size-100);
  --ds-slider-track-size: var(--dimension-size-050);
}

:host(.slider--vertical) {
  width: fit-content;
  min-width: var(--dimension-form-width-xs);
}

.slider__label-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--dimension-space-100);
  min-width: 0;
}

.slider__label {
  min-width: 0;
}

.slider__value {
  flex-shrink: 0;
}

.slider__control {
  position: relative;
  width: 100%;
  height: var(--ds-slider-control-size);
  border-radius: var(--dimension-radius-half);
  cursor: default;
  touch-action: none;
  user-select: none;
}

:host(.slider--vertical) .slider__control {
  align-self: center;
  width: var(--ds-slider-control-size);
  height: var(--ds-slider-vertical-length);
}

:host(.slider--readonly) .slider__control {
  cursor: default;
}

/* Inactive: `.ds-control-inactive` (50% opacity) on the host. */
:host(.ds-control-inactive) .slider__control {
  cursor: not-allowed;
}

.slider__rail {
  position: absolute;
  top: 50%;
  right: 0;
  left: 0;
  height: var(--ds-slider-track-size);
  overflow: hidden;
  border-radius: var(--dimension-radius-025);
  background-color: transparent;
  box-shadow: inset 0 0 0 var(--ds-slider-track-stroke) var(--color-border-secondary);
  transform: translateY(-50%);
  pointer-events: none;
}

:host(.slider--thumb-center) .slider__rail {
  right: calc(var(--ds-slider-thumb-size) / 2);
  left: calc(var(--ds-slider-thumb-size) / 2);
}

.slider__indicator {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: calc(
    var(--ds-slider-thumb-size) / 2 +
      (var(--ds-slider-end-pct, 0) / 100) * (100% - var(--ds-slider-thumb-size))
  );
  border-radius: inherit;
  background-color: var(--color-background-bold-brand);
}

:host(.slider--range) .slider__indicator {
  left: calc(
    var(--ds-slider-thumb-size) / 2 +
      (var(--ds-slider-start-pct, 0) / 100) * (100% - var(--ds-slider-thumb-size))
  );
  width: calc(
    ((var(--ds-slider-end-pct, 0) - var(--ds-slider-start-pct, 0)) / 100) *
      (100% - var(--ds-slider-thumb-size))
  );
}

:host(.slider--thumb-center) .slider__indicator {
  left: calc(var(--ds-slider-start-pct, 0) * 1%);
  width: calc((var(--ds-slider-end-pct, 0) - var(--ds-slider-start-pct, 0)) * 1%);
}

.slider__thumb {
  position: absolute;
  top: 50%;
  left: calc((var(--ds-slider-thumb-pct, 0) / 100) * (100% - var(--ds-slider-thumb-size)));
  width: var(--ds-slider-thumb-size);
  height: var(--ds-slider-thumb-size);
  transform: translateY(-50%);
  z-index: 1;
}

.slider__thumb--active {
  z-index: 2;
}

.slider__input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  border: 0;
  opacity: 0;
  appearance: none;
  pointer-events: none;
  z-index: 2;
}

.slider__thumb-visual {
  position: absolute;
  inset: 0;
  box-sizing: border-box;
  overflow: visible;
  border-radius: var(--dimension-radius-025);
  background-color: var(--color-background-primary);
  box-shadow: var(--effect-elevation-elevated-sm);
  pointer-events: none;
  transition:
    background-color var(--effect-motion-short-2),
    box-shadow var(--effect-motion-short-2);
}

.slider__thumb-wash {
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background-color: transparent;
  transition: background-color var(--effect-motion-short-2);
}

@media (hover: hover) and (pointer: fine) {
  :host(:not(.slider--readonly):not(.ds-control-inactive))
    .slider__thumb:hover
    .slider__thumb-wash {
    background-color: var(--color-interaction-hover);
  }
}

:host([data-dragging]:not(.slider--readonly):not(.ds-control-inactive))
  .slider__thumb--active .slider__thumb-wash {
  background-color: var(--color-interaction-pressed);
}

.slider__input:focus-visible + .slider__thumb-visual,
:host([data-review-focus]) .slider__thumb[data-index='0'] .slider__thumb-visual {
  outline: var(--dimension-stroke-width-025) solid var(--color-interaction-focus);
  outline-offset: var(--dimension-space-025);
}

:host(.slider--vertical) .slider__rail {
  top: 0;
  right: auto;
  bottom: 0;
  left: 50%;
  width: var(--ds-slider-track-size);
  height: auto;
  transform: translateX(-50%);
}

:host(.slider--vertical.slider--thumb-center) .slider__rail {
  top: calc(var(--ds-slider-thumb-size) / 2);
  bottom: calc(var(--ds-slider-thumb-size) / 2);
}

:host(.slider--vertical) .slider__indicator {
  top: auto;
  right: 0;
  bottom: 0;
  left: 0;
  width: auto;
  height: calc(
    var(--ds-slider-thumb-size) / 2 +
      (var(--ds-slider-end-pct, 0) / 100) * (100% - var(--ds-slider-thumb-size))
  );
}

:host(.slider--vertical.slider--range) .slider__indicator {
  bottom: calc(
    var(--ds-slider-thumb-size) / 2 +
      (var(--ds-slider-start-pct, 0) / 100) * (100% - var(--ds-slider-thumb-size))
  );
  height: calc(
    ((var(--ds-slider-end-pct, 0) - var(--ds-slider-start-pct, 0)) / 100) *
      (100% - var(--ds-slider-thumb-size))
  );
}

:host(.slider--vertical.slider--thumb-center) .slider__indicator {
  bottom: calc(var(--ds-slider-start-pct, 0) * 1%);
  height: calc((var(--ds-slider-end-pct, 0) - var(--ds-slider-start-pct, 0)) * 1%);
}

:host(.slider--vertical) .slider__thumb {
  top: auto;
  bottom: calc((var(--ds-slider-thumb-pct, 0) / 100) * (100% - var(--ds-slider-thumb-size)));
  left: 50%;
  transform: translateX(-50%);
}

@media (prefers-reduced-motion: reduce) {
  .slider__thumb-visual,
  .slider__thumb-wash {
    transition: none;
  }
}

@media (forced-colors: active) {
  .slider__rail {
    border: var(--ds-slider-track-stroke) solid var(--ds-forced-color-content);
    box-shadow: none;
  }

  .slider__indicator {
    background-color: var(--ds-forced-color-selected);
  }

  .slider__thumb-visual {
    border:
      var(--dimension-stroke-width-012) solid
      var(--ds-forced-color-control-boundary);
    background-color: var(--ds-forced-color-surface);
    box-shadow: none;
  }

  .slider__input:focus-visible + .slider__thumb-visual,
  :host([data-review-focus]) .slider__thumb[data-index='0'] .slider__thumb-visual {
    outline-color: var(--ds-forced-color-selected);
  }
}
